import { Card, Descriptions, Modal, Skeleton, Typography } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useMessage } from "../../../uteis/mensagens/useMessage";
import { formatValorReal } from "../../../uteis/formatValorReal";
import {
  buscarPrecificacaoCompatibilidadeService,
  PrecificacaoCompatibilidadeResponse,
} from "../../../services/buscarPrecificacaoCompatibilidadeService";
import { Title } from "../styles";

const { Text } = Typography;

interface ModalCondicoesPagamentoProps {
  isModalOpen: boolean;
  handleFechar: () => void;
  compatibilidadeId: number | null;
}

export default function ModalCondicoesPagamento({
  isModalOpen,
  handleFechar,
  compatibilidadeId,
}: ModalCondicoesPagamentoProps) {
  const { showWarning, showError, contextHolder } = useMessage();
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<PrecificacaoCompatibilidadeResponse | null>(
    null
  );

  useEffect(() => {
    const buscar = async () => {
      if (!isModalOpen || !compatibilidadeId) {
        return;
      }

      setLoading(true);
      setDados(null);

      try {
        const response = await buscarPrecificacaoCompatibilidadeService(
          compatibilidadeId
        );

        if (!response) {
          showError("Não foi possível carregar as condições de pagamento.");
          handleFechar();
          return;
        }

        if (!response.possuiValorDisponivel) {
          showWarning(
            response.mensagem || "O período não possui valor disponível."
          );
          handleFechar();
          return;
        }

        setDados(response);
      } catch (error) {
        showError("Não foi possível carregar as condições de pagamento.");
        handleFechar();
      } finally {
        setLoading(false);
      }
    };

    buscar();
  }, [isModalOpen, compatibilidadeId]);

  const formatarDataHora = (value?: string) =>
    value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—";

  const formatarData = (value?: string) =>
    value ? dayjs(value).format("DD/MM/YYYY") : "—";

  return (
    <>
      {contextHolder}
      <Modal
        open={isModalOpen}
        onCancel={handleFechar}
        footer={null}
        width={720}
        destroyOnClose
      >
        {loading ? (
          <Skeleton active />
        ) : dados ? (
          <Card
            title={<Title>Condições de pagamento</Title>}
            style={{
              width: "100%",
              marginTop: "20px",
              borderRadius: "10px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Compatibilidade">
                {dados.compatibilidadeId}
              </Descriptions.Item>
              <Descriptions.Item label="Valor disponível">
                {formatValorReal(dados.valorDisponivel)}
              </Descriptions.Item>
              <Descriptions.Item label="Data do cálculo">
                {formatarDataHora(dados.dataCalculo)}
              </Descriptions.Item>
              <Descriptions.Item label="Check-in">
                {formatarData(dados.dataCheckin)}
              </Descriptions.Item>
              <Descriptions.Item label="Check-out">
                {formatarData(dados.dataCheckout)}
              </Descriptions.Item>
              <Descriptions.Item label="PIX">
                {formatValorReal(dados.pagamento?.pix?.valor)}
              </Descriptions.Item>
              <Descriptions.Item label="Parcelas no cartão">
                {dados.pagamento?.cartao?.quantidadeParcelas ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Valor da parcela">
                {formatValorReal(dados.pagamento?.cartao?.valorParcela)}
              </Descriptions.Item>
              <Descriptions.Item label="Total à vista no cartão">
                {formatValorReal(dados.pagamento?.cartao?.valorTotal)}
              </Descriptions.Item>
            </Descriptions>
            <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
              Valores calculados com base no valor disponível e na data atual.
            </Text>
          </Card>
        ) : null}
      </Modal>
    </>
  );
}
